import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentApi } from '@services/appointment.service'
import type { DoctorAvailabilityOverride } from '@/types'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Switch } from '@components/ui/switch'
import { Calendar } from '@components/ui/calendar'
import { Badge } from '@components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import { Pencil, Trash2, Plus, BanIcon, Clock } from 'lucide-react'
import { cn } from '@lib/utils'
import { TIME_OPTIONS, parseLocalDate, toISODate, formatDisplayDate } from './time-utils'

interface Props { doctorId: string }

function sortByDate(items: DoctorAvailabilityOverride[]): DoctorAvailabilityOverride[] {
  return [...items].sort((a, b) => a.override_date.localeCompare(b.override_date))
}

export function ExceptionsTab({ doctorId }: Props) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [date, setDate] = useState('')
  const [isClosed, setIsClosed] = useState(true)
  const [startTime, setStartTime] = useState('09:00')
  const [endTime, setEndTime] = useState('17:00')
  const [note, setNote] = useState('')

  const { data } = useQuery({
    queryKey: ['availability-overrides', doctorId],
    queryFn: () => appointmentApi.getAvailabilityOverrides(doctorId),
  })

  const overrides = sortByDate(data?.items ?? [])
  const closedDates = overrides.filter((o) => o.is_closed).map((o) => parseLocalDate(o.override_date))
  const partialDates = overrides.filter((o) => !o.is_closed).map((o) => parseLocalDate(o.override_date))

  const reset = () => {
    setEditingId(null)
    setDate('')
    setIsClosed(true)
    setStartTime('09:00')
    setEndTime('17:00')
    setNote('')
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editingId) await appointmentApi.deleteAvailabilityOverride(editingId)
      return appointmentApi.createAvailabilityOverride({
        doctor_id: doctorId,
        override_date: date,
        is_closed: isClosed,
        ...(isClosed ? {} : { start_time: startTime, end_time: endTime }),
        ...(note ? { note } : {}),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-overrides', doctorId] })
      reset()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => appointmentApi.deleteAvailabilityOverride(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['availability-overrides', doctorId] }),
  })

  const startEdit = (o: DoctorAvailabilityOverride) => {
    setEditingId(o.id)
    setDate(o.override_date)
    setIsClosed(o.is_closed)
    setStartTime(o.start_time ?? '09:00')
    setEndTime(o.end_time ?? '17:00')
    setNote(o.note ?? '')
  }

  const today = new Date().toISOString().slice(0, 10)
  const upcoming = overrides.filter((o) => o.override_date >= today)
  const past = overrides.filter((o) => o.override_date < today)

  return (
    <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
      <div className="space-y-4">
        <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <div className="border-b bg-amber-500/5 px-4 py-2.5" style={{ borderTopColor: '#d97706', borderTopWidth: '2px' }}>
            <h3 className="text-sm font-semibold text-amber-900">
              {editingId ? 'Editar excepción' : 'Agregar excepción'}
            </h3>
          </div>
          <div className="p-4">

          <div className="space-y-1">
            <Label className="text-xs">Calendario</Label>
            <div className="flex justify-center rounded-lg border">
              <Calendar
                mode="single"
                selected={date ? parseLocalDate(date) : undefined}
                onSelect={(d) => d && setDate(toISODate(d))}
                className="p-2 text-xs [&_button]:min-h-0"
                style={{
                  '--rdp-day-width': '1.75rem',
                  '--rdp-day-height': '1.75rem',
                  '--rdp-day_button-width': '1.75rem',
                  '--rdp-day_button-height': '1.75rem',
                } as React.CSSProperties}
                modifiers={{ closed: closedDates, partial: partialDates }}
                modifiersStyles={{
                  closed: { backgroundColor: 'rgb(239 68 68 / 0.12)', color: 'rgb(185 28 28)', fontWeight: 700 },
                  partial: { color: 'rgb(var(--ml-primary))', fontWeight: 700, textDecoration: 'underline' },
                }}
              />
            </div>
            <div className="flex items-center gap-3 pt-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-red-400/80" />
                Cerrado
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-ml-primary/60" />
                Reducido
              </span>
            </div>
          </div>

          <div className="mt-3 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Nota (opcional)</Label>
              <Input
                placeholder="Ej: Congreso médico"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-8 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={isClosed} onCheckedChange={setIsClosed} />
              <Label className="text-sm">Cerrado todo el día</Label>
            </div>

            {!isClosed && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Inicio</Label>
                  <Select value={startTime} onValueChange={setStartTime}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fin</Label>
                  <Select value={endTime} onValueChange={setEndTime}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.filter((o) => o.value > startTime).map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                className="flex-1"
                onClick={() => saveMutation.mutate()}
                disabled={!date || saveMutation.isPending}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {saveMutation.isPending ? 'Guardando…' : editingId ? 'Actualizar' : 'Agregar'}
              </Button>
              {editingId && (
                <Button size="sm" variant="outline" onClick={reset}>
                  Cancelar
                </Button>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {overrides.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed bg-card px-6 py-12 text-center">
            <BanIcon className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm font-medium">Sin excepciones programadas</p>
            <p className="text-xs text-muted-foreground">
              Usa el calendario para marcar días no disponibles o con horario reducido
            </p>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <Section label="Próximas" items={upcoming} onEdit={startEdit} onDelete={(id) => deleteMutation.mutate(id)} />
            )}
            {past.length > 0 && (
              <Section label="Pasadas" items={past} onEdit={startEdit} onDelete={(id) => deleteMutation.mutate(id)} muted />
            )}
          </>
        )}
      </div>
    </div>
  )
}

function Section({
  label,
  items,
  onEdit,
  onDelete,
  muted = false,
}: {
  label: string
  items: DoctorAvailabilityOverride[]
  onEdit: (o: DoctorAvailabilityOverride) => void
  onDelete: (id: string) => void
  muted?: boolean
}) {
  return (
    <div>
      <p className={cn('mb-2 text-xs font-semibold uppercase tracking-wide', muted ? 'text-muted-foreground/60' : 'text-muted-foreground')}>
        {label}
      </p>
      <div className={cn('divide-y overflow-hidden rounded-xl border bg-card shadow-sm', muted && 'opacity-60')}>
        {items.map((o) => (
          <div
            key={o.id}
            className={cn(
              'flex items-center gap-3 px-4 py-3',
              o.is_closed ? 'bg-destructive/5' : 'bg-[#2d7a5e]/5'
            )}
          >
            <div className="shrink-0">
              {o.is_closed ? (
                <BanIcon className="h-4 w-4 text-destructive/70" />
              ) : (
                <Clock className="h-4 w-4 text-ml-primary/70" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">
                {formatDisplayDate(o.override_date, { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <p className="text-xs text-muted-foreground">
                {o.is_closed ? (
                  <span className="text-destructive/80">Cerrado todo el día</span>
                ) : (
                  `${o.start_time} – ${o.end_time}`
                )}
                {o.note && <span className="ml-1">· {o.note}</span>}
              </p>
            </div>
            <Badge
              variant="outline"
              className={cn(
                'shrink-0 text-xs',
                o.is_closed
                  ? 'border-destructive/20 bg-destructive/5 text-destructive'
                  : 'border-ml-primary/20 bg-ml-primary/5 text-ml-primary'
              )}
            >
              {o.is_closed ? 'Cerrado' : 'Reducido'}
            </Badge>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => onEdit(o)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => onDelete(o.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
